import { createAction, PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'

import { WhatsAppApi } from 'shared/api/requests/whatsApp'
import {
  MarketingAnalyticsPeriod,
  WaMarketingAnalyticsResponse,
  WaMarketingTemplateWithInsights,
} from 'shared/api/requests/whatsApp/schemas'
import { createListSlice } from 'shared/lib/factory/redux/createListSlice'
import { createListOperations } from 'shared/lib/factory/redux/createListSlice/lib/createListOperations'

import { WhatsAppMarketingAnalyticsExtraState } from '../interfaces/whatsAppMarketingAnalytics'

const NAMESPACE = 'whatsAppMarketingAnalytics'
const STORE_PATH = 'whatsApp.marketingAnalytics'
const LIST_KEY = 'message_templates'

const extraState: WhatsAppMarketingAnalyticsExtraState = {
  period: MarketingAnalyticsPeriod.LAST_7_DAYS,
  total: null,
  lastUpdatedAt: null,
}

const getMarketingMessageTemplatesAnalytics = async ({
  period,
}: {
  period: MarketingAnalyticsPeriod
}): Promise<WaMarketingAnalyticsResponse> => {
  const result = await WhatsAppApi.getMarketingMessageTemplatesAnalytics({
    body: { date_range: period },
  })
  return result.data
}

const getListFulfilled = createAction<WaMarketingAnalyticsResponse>(
  `@${NAMESPACE}List/fetch/fulfilled`,
)

export const marketingAnalyticsSlice = createListSlice({
  namespace: NAMESPACE,
  storePath: STORE_PATH,
  listKey: LIST_KEY,

  operations: createListOperations({
    getList: getMarketingMessageTemplatesAnalytics,
    getNextList: undefined,
    getPrevList: undefined,
    createItem: undefined,
    updateItem: undefined,
    deleteItem: undefined,
  }),

  getItemId: (item: WaMarketingTemplateWithInsights) => item.message_template.template_id,

  extraState,

  reducers: {
    setPeriod(state, action: PayloadAction<MarketingAnalyticsPeriod>) {
      state.period = action.payload
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getListFulfilled, (state, action) => {
      if (!action.payload) return
      state.total = action.payload.total
      state.lastUpdatedAt = Date.now()
    })
  },
})

export const {
  reducer: marketingAnalyticsReducer,
  actions: marketingAnalyticsActions,
  selectors: marketingAnalyticsBaseSelectors,
  initialState: marketingAnalyticsInitialState,
} = marketingAnalyticsSlice

/**
 * Get the period of the analytics
 */
export const getMarketingAnalyticsPeriod = (state: RootState) =>
  state.whatsApp.marketingAnalytics.period

/**
 * Get aggregated analytics across all templates
 */
export const getAggregatedAnalytics = (state: RootState) => state.whatsApp.marketingAnalytics.total

/**
 * Timestamp (ms) of the last successful analytics fetch, or null if the data
 * hasn't been loaded yet.
 */
export const getMarketingAnalyticsLastUpdatedAt = (state: RootState) =>
  state.whatsApp.marketingAnalytics.lastUpdatedAt

/**
 * Get list of message templates with analytics data
 */
export const getMarketingAnalyticsTemplates = marketingAnalyticsBaseSelectors.getList

/**
 * Get analytics data across all templates as a map
 */
export const getMarketingAnalyticsMap = marketingAnalyticsBaseSelectors.getMap

/**
 * Get message template with analytics data by template id
 */
export const getMarketingAnalyticsTemplateById = marketingAnalyticsBaseSelectors.getItemById

export const getIsMarketingAnalyticsLoading = marketingAnalyticsBaseSelectors.getIsListLoading

export const getIsMarketingAnalyticsLoaded = marketingAnalyticsBaseSelectors.getIsListLoaded

export const getIsMarketingAnalyticsError = marketingAnalyticsBaseSelectors.getIsError

export const getIsMarketingAnalyticsEmpty = (state: RootState) =>
  marketingAnalyticsBaseSelectors.getIsListLoaded(state) &&
  marketingAnalyticsBaseSelectors.getList(state).length === 0

export enum MarketingAnalyticsViewStatus {
  LOADING = 'loading',
  ERROR = 'error',
  EMPTY = 'empty',
  READY = 'ready',
}

export const getMarketingAnalyticsViewStatus = createSelector(
  [
    getIsMarketingAnalyticsLoading,
    getIsMarketingAnalyticsError,
    getIsMarketingAnalyticsEmpty,
    getIsMarketingAnalyticsLoaded,
  ],
  (isLoading, isError, isEmpty, isLoaded): MarketingAnalyticsViewStatus | null => {
    if (isLoading) return MarketingAnalyticsViewStatus.LOADING
    if (isError) return MarketingAnalyticsViewStatus.ERROR
    if (isEmpty) return MarketingAnalyticsViewStatus.EMPTY
    if (isLoaded) return MarketingAnalyticsViewStatus.READY
    return null
  },
)

export const getAggregatedAnalyticsViewStatus = createSelector(
  [getMarketingAnalyticsViewStatus, getAggregatedAnalytics],
  (status, insights): MarketingAnalyticsViewStatus | null => {
    // If the list is loaded but no meaningful metrics are available, treat the status as empty rather than READY.
    if (status === MarketingAnalyticsViewStatus.READY && (!insights || insights.sent <= 0)) {
      return MarketingAnalyticsViewStatus.EMPTY
    }
    return status
  },
)
